import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { BookOpen } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const r = await login(email, password);
    setBusy(false);
    if (r.ok) {
      toast.success("Welcome back.");
      nav("/dashboard");
    } else {
      toast.error(r.error);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background" data-testid="login-page">
      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1514369118554-e20d93546b30?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxkb2N0b3JhbCUyMHN0dWRlbnQlMjBzdHVkeWluZ3xlbnwwfHx8fDE3ODYyOTMwMzJ8MA&ixlib=rb-4.1.0&q=85"
          alt="Writing"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-secondary/40" />
        <div className="relative z-10 h-full flex flex-col justify-end p-12">
          <BookOpen className="h-6 w-6 text-primary mb-4" />
          <h2 className="font-serif text-4xl font-black tracking-tight max-w-md">
            Return to your circle.
          </h2>
          <p className="text-muted-foreground mt-3 max-w-md">
            Your communities, mentors, and companion are waiting exactly where you left them.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16 lg:px-16">
        <form onSubmit={submit} className="w-full max-w-md space-y-6" data-testid="login-form">
          <div className="flex items-center gap-2 lg:hidden">
            <BookOpen className="h-5 w-5 text-primary" />
            <span className="font-serif text-xl font-black">DocCircle</span>
          </div>
          <div>
            <h1 className="font-serif text-3xl font-black tracking-tight">Sign in</h1>
            <p className="text-sm text-muted-foreground mt-2">
              New here?{" "}
              <Link to="/register" className="text-primary underline underline-offset-4">
                Create an account
              </Link>
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                required
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
              <div className="mt-1 text-right">
                <Link
                  to="/forgot-password"
                  className="text-xs text-primary hover:underline underline-offset-4"
                  data-testid="login-forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full rounded-full"
            disabled={busy}
            data-testid="login-submit-btn"
          >
            {busy ? "Signing in…" : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
}
